import React from "react";

/**
 * Consent-text link tokens: `{{N:Label}}`.
 *
 * - `N` (1-based) selects which configured link backs the token — slot 1 is the
 *   first entry in `links`, slot 2 the second, and so on.
 * - `Label` (everything after the first `:`) is the visible link text.
 *
 * e.g. `I agree to the {{1:Terms & Conditions}} and {{2:Privacy Policy}}`.
 */
const CONSENT_TOKEN_RE = /\{\{\s*(\d+)\s*:\s*([^}]*?)\s*\}\}/g;

/** Block script-y URL schemes; everything else (http(s), mailto, tel, relative) is allowed. */
function isSafeHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  return (
    !v.startsWith("javascript:") &&
    !v.startsWith("data:") &&
    !v.startsWith("vbscript:")
  );
}

export interface RenderConsentTextOptions {
  /** className applied to each generated `<a>` (defaults to a plain underline). */
  linkClassName?: string;
  /**
   * Link color — pass the form's checkbox color. When it's a non-empty value the
   * link renders in that color; when empty/undefined the link inherits the
   * surrounding text color and renders bold instead (so a link is still visually
   * distinct even when no checkbox color is configured).
   */
  linkColor?: string | null;
}

/**
 * Render consent copy that may contain `{{N:Label}}` link tokens.
 *
 * `links` is ordered by slot: `links[0]` backs `{{1:…}}`, `links[1]` backs
 * `{{2:…}}`, etc. A token whose slot has a non-empty, safe URL renders as an
 * `<a target="_blank" rel="noopener noreferrer">`; otherwise its Label renders
 * as plain text (so an unconfigured link degrades gracefully). Text with no
 * tokens is returned unchanged, keeping existing consent copy identical.
 *
 * Output is built from React nodes (never `dangerouslySetInnerHTML`), so the
 * admin-authored Label/text cannot inject markup.
 */
export function renderConsentText(
  text: string | null | undefined,
  links: Array<string | null | undefined>,
  { linkClassName = "underline", linkColor }: RenderConsentTextOptions = {},
): React.ReactNode {
  if (!text) return text ?? null;
  // Fast path: no possible token, return the original string untouched.
  if (!text.includes("{{")) return text;

  // Checkbox color drives the link color; with no color, links inherit the
  // surrounding text color and turn bold so they stay visually distinct.
  const hasColor = !!(linkColor && String(linkColor).trim());
  const anchorClassName = hasColor ? linkClassName : `${linkClassName} font-bold`;
  const anchorStyle = hasColor ? { color: String(linkColor) } : undefined;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let linkKey = 0;
  CONSENT_TOKEN_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = CONSENT_TOKEN_RE.exec(text)) !== null) {
    const [full, slot, label] = match;

    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const href = (links[Number(slot) - 1] ?? "").toString().trim();
    if (href && isSafeHref(href)) {
      nodes.push(
        <a
          key={`consent-link-${linkKey++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={anchorClassName}
          style={anchorStyle}
        >
          {label}
        </a>,
      );
    } else {
      // No URL configured for this slot (or unsafe) — show the label as text.
      nodes.push(label);
    }

    lastIndex = match.index + full.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}
